import { useEffect, useMemo, useRef } from "react"
import * as L from "leaflet/src/Leaflet"
import { antPath } from "leaflet-ant-path"

// Leaflet is imported directly here rather than relied on as a `window.L`
// global set by a separate pack, so map setup no longer depends on script
// load order relative to this component's mount.
export default function LocationMap({ locations, markerIconUrl, tabButtonId }) {
  const containerRef = useRef(null)
  const pathLatLngs = useMemo(
    () =>
      [...locations].reverse().map(({ latitude, longitude }) => [latitude, longitude]),
    [locations],
  )

  useEffect(() => {
    if (!containerRef.current || locations.length === 0) return

    const map = L.map(containerRef.current)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      referrerPolicy: "origin-when-cross-origin",
      subdomains: ["a", "b", "c"],
    }).addTo(map)

    let path
    if (locations.length > 1) {
      path = antPath(pathLatLngs, {
        use: L.polygon,
        color: "#bbb",
        pulseColor: "#555",
        dashArray: [100, 30],
        delay: 800,
        hardwareAccelerated: true,
      })
      map.addLayer(path)
    }

    locations.forEach((location, index) => {
      const marker =
        index === 0
          ? L.marker([location.latitude, location.longitude], {
              icon: L.icon({
                iconUrl: markerIconUrl,
                iconSize: [30, 43],
                iconAnchor: [15, 43],
              }),
              title: location.mapTitle,
            })
          : L.circleMarker([location.latitude, location.longitude], {
              color: "black",
              fill: true,
              fillColor: "#f75448",
              fillOpacity: 1,
              radius: 5,
              weight: 1,
            })

      marker.bindTooltip(location.mapTitle).addTo(map)
    })

    const setSize = () => {
      map.invalidateSize(true)

      if (pathLatLngs.length > 1) {
        map.fitBounds(path.getBounds())
      } else {
        map.setView(pathLatLngs[0], 14)
      }
    }

    const tabButton = tabButtonId ? document.getElementById(tabButtonId) : null
    tabButton && $(tabButton).on("shown.bs.tab", setSize)
    setSize()
    const timeoutId = setTimeout(setSize, 100)

    return () => {
      clearTimeout(timeoutId)
      tabButton && $(tabButton).off("shown.bs.tab", setSize)
      map.remove()
    }
  }, [locations, markerIconUrl, pathLatLngs, tabButtonId])

  if (locations.length === 0) return null

  return <div ref={containerRef} id="map-canvas" />
}
